import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
  Ref,
  RefObject,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRef } from 'react';
import { VariantProps, tv } from 'tailwind-variants';

import Text from '@cartwhl/ui/base/Text';
import useElementSize from '@cartwhl/ui/hooks/useElementSize';

import CrossIcon from '../icons/CrossIcon';
import { WIZARD_TARGET_ATTRIBUTE } from '../wizard/constants';

const CONTAINER_SIZE_DURATION = 200;
const FADE_CONTENT_IN_DURATION = 300;
const FADE_CONTENT_OUT_DURATION = 150;

type FrontDoorSize = 'lg' | 'sm';

// #region Context

interface FrontDoorContextType {
  containerTransitionDelay: number;
  isUnmounting: boolean;
  size: FrontDoorSize;
  setContainerTransitionDelay: (delay: number) => void;
  setIsUnmounting: (isUnmounting: boolean) => void;
}

const FrontDoorContext = createContext<FrontDoorContextType | undefined>(
  undefined,
);

function useFrontDoorContext() {
  const context = useContext(FrontDoorContext);
  if (context === undefined) {
    throw new Error(
      'useFrontDoorContext must be used within a FrontDoorProvider',
    );
  }
  return context;
}

// #endregion Context

// #region Container

const containerContent = tv({
  base: 'relative z-10 flex flex-col items-center gap-2 px-2 py-2',
});

export function Container({
  children,
  showX,
  onXClick,
  ref,
  className,
  showBackground = true,
  size = 'lg',
  wizardTargetId,
}: {
  children: ReactNode;
  showX?: boolean;
  onXClick?: () => void;
  ref?: RefObject<HTMLDivElement | null>;
  className?: string;
  showBackground?: boolean;
  size?: FrontDoorSize;
  /** When set, the padded content region matches the frosted overlay for wizard spotlight. */
  wizardTargetId?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerTransitionDelay, setContainerTransitionDelay] = useState(0);
  const [isUnmounting, setIsUnmounting] = useState(false);

  const { width: contentWidth, height: contentHeight } =
    useElementSize(contentRef);

  return (
    <FrontDoorContext.Provider
      value={{
        containerTransitionDelay,
        setContainerTransitionDelay,
        isUnmounting,
        setIsUnmounting,
        size,
      }}
    >
      <div
        className={`pointer-events-none flex h-full w-full flex-col items-center justify-end ${className}`}
        ref={ref}
      >
        <div className="pointer-events-auto relative z-0 w-fit max-w-full lg:w-fit">
          {showBackground && (
            <div
              className="bg-player-overlay shadow-player-overlay absolute bottom-0 left-1/2 z-0 -translate-x-1/2 rounded-2xl transition-all lg:rounded-[24px]"
              style={{
                width: `${contentWidth}px`,
                height: `${contentHeight}px`,
                transitionDuration: `${CONTAINER_SIZE_DURATION}ms`,
                transitionDelay: `${containerTransitionDelay}ms`,
              }}
            />
          )}
          <div
            className={containerContent({
              className: wizardTargetId
                ? 'rounded-2xl lg:rounded-[24px]'
                : undefined,
            })}
            ref={contentRef}
            {...(wizardTargetId
              ? { [WIZARD_TARGET_ATTRIBUTE]: wizardTargetId }
              : {})}
          >
            {children}

            {showX && (
              <div className="self-stretch lg:hidden">
                <Button text="Back" className="w-full" onClick={onXClick} />
              </div>
            )}
          </div>
          <div
            className={`absolute bottom-0 left-full hidden px-2 py-2 transition-all lg:block ${showX ? 'visible opacity-100' : 'invisible opacity-0'}`}
          >
            <Button
              variant="secondary"
              icon={<CrossIcon />}
              className="pointer-events-auto flex w-[30px] items-center justify-center rounded-2xl bg-white/25 transition-colors hover:bg-white/50"
              onClick={onXClick}
            />
          </div>
        </div>
      </div>
    </FrontDoorContext.Provider>
  );
}

// #endregion Container

// #region FadeContent

const FadeContent = ({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) => {
  const [mounted, setMounted] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const { setContainerTransitionDelay, setIsUnmounting, isUnmounting } =
    useFrontDoorContext();

  const initialMounted = useRef(false);

  useEffect(() => {
    const update = async () => {
      if (show) {
        await delay(FADE_CONTENT_OUT_DURATION);
        setContainerTransitionDelay(0);
        setMounted(true);
        await delay(FADE_CONTENT_IN_DURATION);
        setOpacity(1);
      } else if (!show && initialMounted.current) {
        setIsUnmounting(true);
        setOpacity(0);
        setContainerTransitionDelay(FADE_CONTENT_OUT_DURATION);
        setMounted(true);
        await delay(FADE_CONTENT_OUT_DURATION);
        setMounted(false);
        setIsUnmounting(false);
      }
    };

    update();

    initialMounted.current = true;
  }, [show]);

  if (!mounted) return null;

  return (
    <div
      className={`self-stretch transition-all ${className}`}
      style={{
        opacity,
        transform: opacity ? `translateY(0px)` : `translateY(6px)`,
        transitionDuration: `${show ? FADE_CONTENT_IN_DURATION : FADE_CONTENT_OUT_DURATION}ms`,
      }}
    >
      {children}
    </div>
  );
};

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// #endregion FadeContent

// #region Button

const button = tv({
  base: 'text-gray-90 flex min-w-0 items-center justify-center rounded-lg transition-all duration-150 active:scale-[0.98] dark:text-white',
  variants: {
    variant: {
      go: 'bg-green/80 dark:bg-green/60',
      primary: 'bg-blue text-white',
      secondary: 'dark:bg-gray-90/55 bg-white/55',
      tertiary: 'dark:bg-gray-90/55 bg-white/25',
    },
    size: {
      lg: 'h-10 gap-2',
      sm: 'h-[30px] gap-1.5',
    },
    padding: {
      textOnly: 'px-3',
      iconOnly: 'px-2.5',
      iconAndText: 'px-3 pl-2.5',
      wide: 'pl-[22px] pr-6',
    },
    isActive: {
      true: 'bg-white text-gray-90 dark:text-gray-90',
      false: '',
    },
    rounded: {
      first: 'lg:rounded-l-2xl',
      last: 'lg:rounded-r-2xl',
      both: 'lg:rounded-2xl',
    },
    disabled: {
      true: 'opacity-50',
      false: '',
    },
  },
  compoundVariants: [
    // Hover styles:
    {
      variant: ['secondary', 'tertiary'],
      disabled: false,
      className: 'dark:hover:bg-gray-90 cursor-pointer hover:bg-white',
    },
    {
      variant: 'primary',
      disabled: false,
      className: 'cursor-pointer hover:bg-[#166ADC]',
    },
    {
      variant: 'go',
      disabled: false,
      className: 'hover:bg-green dark:hover:bg-green/80 cursor-pointer',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'lg',
    disabled: false,
  },
});

interface FrontDoorButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  text?: string;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  isActive?: boolean;
  variant?: VariantProps<typeof button>['variant'];
  size?: VariantProps<typeof button>['size'];
  rounded?: VariantProps<typeof button>['rounded'];
  isWide?: boolean;
  style?: CSSProperties;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  text,
  icon,
  iconEnd,
  isActive,
  className,
  variant,
  size,
  rounded,
  isWide,
  style,
  disabled,
  ref,
  type = 'button',
  ...props
}: FrontDoorButtonProps) {
  const context = useContext(FrontDoorContext);

  return (
    <Text
      as="button"
      variant="studio/body/medium/sm"
      className={button({
        variant,
        size: size ?? context?.size,
        padding: getButtonPadding(text, icon, iconEnd, isWide),
        rounded,
        isActive,
        disabled,
        className,
      })}
      style={style}
      type={type}
      disabled={disabled}
      ref={ref}
      {...props}
    >
      {icon}
      {text && <span className="truncate">{text}</span>}
      {iconEnd}
    </Text>
  );
}

function getButtonPadding(
  text?: string,
  icon?: ReactNode,
  iconEnd?: ReactNode,
  isWide?: boolean,
) {
  if (isWide) return 'wide';
  if (text && (icon || iconEnd)) return 'iconAndText';
  if (text) return 'textOnly';
  return 'iconOnly';
}

// #endregion Button

// #region Divider

const divider = tv({
  base: 'mx-2 w-px flex-none border-0 bg-white/30',
  variants: {
    size: {
      lg: 'h-10',
      sm: 'h-[30px]',
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

const Divider = ({
  className,
  size,
}: {
  className?: string;
  size?: VariantProps<typeof divider>['size'];
}) => {
  const context = useContext(FrontDoorContext);

  return (
    <hr
      className={divider({
        size: size ?? context?.size,
        className,
      })}
    />
  );
};

// #endregion Divider

export default {
  Container,
  FadeContent,
  Button,
  Divider,
};
